import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Avatar, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/components/auth/AuthProvider';
import { useNotification } from '@/components/ui-library/NotificationProvider';
import useApi from '@/hooks/useApi';
import { useDirection } from '@/hooks/useDirection';
import { BORDER_RADIUS, semanticColors, semanticGradients } from '@/theme/tokens';
import { signedInViaLabel } from '@/utils/authProviderName';
import { ltrValueProps } from '@/utils/bidi';

import SectionSaveRow from './SectionSaveRow';
import {
  SettingsRow,
  SettingsSection,
  useSettingsControlHeight,
  useSettingsSurface,
} from './SettingsSection';
import { INPUT_SX } from './settingsStyles';
import type { SettingsPanelProps } from './settingsTypes';

const initialsFor = (name?: string, email?: string): string => {
  const nameParts = name?.trim().split(/\s+/).filter(Boolean);
  if (nameParts && nameParts.length > 1) {
    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
  }
  return (name?.trim() || email || 'U').slice(0, 2).toUpperCase();
};

export function ProfileSettingsSection({ onSignOut }: { onSignOut?: () => void } = {}) {
  const controlHeight = useSettingsControlHeight();
  const { t } = useTranslation('settings');
  const { user, signIn, signOut } = useAuth();
  const dir = useDirection();

  if (!user) {
    return (
      <SettingsSection>
        <SettingsRow
          label={t('browsingAsGuest')}
          description={t('guestAccountDesc')}
          divider={false}
          control={
            <Button
              disableRipple
              onClick={signIn}
              size="small"
              // Same pill and colors as the header and left-pane sign-in buttons.
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                px: 2,
                py: '6px',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.01em',
                whiteSpace: 'nowrap',
                backgroundColor: semanticColors.textPrimary,
                color: semanticColors.textOnPrimary,
                boxShadow: 'none',
                transition: 'background-color 0.2s ease',
                '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
                '&:focus-visible': {
                  outline: `2px solid ${semanticColors.brandPrimary}`,
                  outlineOffset: '2px',
                },
              }}
            >
              {t('signInSignUp')}
            </Button>
          }
        />
      </SettingsSection>
    );
  }

  const signedInVia = signedInViaLabel(user.auth0Id, t);

  return (
    <SettingsSection>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 1 }}>
        <Avatar
          src={user.picture || undefined}
          alt={user.name || user.email || t('common:userAvatarAlt')}
          sx={{
            width: 40,
            height: 40,
            background: user.picture ? undefined : semanticGradients.accountAvatar,
            fontSize: '15px',
            fontWeight: 700,
          }}
        >
          {initialsFor(user.name, user.email)}
        </Avatar>
        <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
          <Typography
            {...ltrValueProps(dir)}
            sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.4, overflowWrap: 'anywhere' }}
          >
            {user.email}
          </Typography>
          {signedInVia && (
            <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary, mt: 0.25 }}>
              {signedInVia}
            </Typography>
          )}
        </Box>
        <Button
          onClick={() => (onSignOut ? onSignOut() : void signOut())}
          variant="outlined"
          size="small"
          startIcon={<ExitToAppIcon sx={{ fontSize: '16px !important' }} />}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            borderRadius: BORDER_RADIUS,
            height: controlHeight,
            px: 1.5,
            flexShrink: 0,
            borderColor: semanticColors.borderLight,
            color: semanticColors.textSecondary,
          }}
        >
          {t('signOut')}
        </Button>
      </Box>
    </SettingsSection>
  );
}

export function NameSettingsSection({ settings }: SettingsPanelProps) {
  const surface = useSettingsSurface();
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const firstNameInput = useRef<HTMLInputElement>(null);
  const lastEditedInput = useRef<HTMLInputElement | null>(null);
  const restoreFocusAfterSave = useRef(false);
  const nameSaveState = settings.groups.name.state;

  const focusNameInput = () => {
    (lastEditedInput.current ?? firstNameInput.current)?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (restoreFocusAfterSave.current && (nameSaveState === 'saved' || nameSaveState === 'error')) {
      restoreFocusAfterSave.current = false;
      (lastEditedInput.current ?? firstNameInput.current)?.focus({ preventScroll: true });
    }
  }, [nameSaveState]);

  if (!user) return null;

  const saveName = () => {
    if (surface === 'dialog') restoreFocusAfterSave.current = true;
    void settings.saveName();
  };

  const saveOnEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && settings.groups.name.canSave) {
      event.preventDefault();
      if (nameSaveState !== 'saving') saveName();
    }
  };

  return (
    <SettingsSection>
      <Typography component="h3" sx={{ fontWeight: 600, fontSize: '15px', lineHeight: 1.4 }}>
        {t('yourName')}
      </Typography>
      <Typography
        sx={{
          fontSize: surface === 'dialog' ? '14px' : '13px',
          color: semanticColors.textTertiary,
          lineHeight: 1.5,
          mt: 0.5,
          mb: 2,
        }}
      >
        {t('yourNameDesc')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <TextField
          id="settings-first-name"
          inputRef={firstNameInput}
          label={t('firstName')}
          fullWidth
          value={settings.firstName}
          onChange={(event) => settings.setFirstName(event.target.value)}
          onKeyDown={saveOnEnter}
          onFocus={(event) => {
            lastEditedInput.current = event.target as HTMLInputElement;
          }}
          autoComplete="given-name"
          inputProps={{ maxLength: 100 }}
          disabled={settings.groups.name.state === 'saving'}
          size="small"
          sx={INPUT_SX}
        />
        <TextField
          id="settings-last-name"
          label={t('lastName')}
          fullWidth
          value={settings.lastName}
          onChange={(event) => settings.setLastName(event.target.value)}
          onKeyDown={saveOnEnter}
          onFocus={(event) => {
            lastEditedInput.current = event.target as HTMLInputElement;
          }}
          autoComplete="family-name"
          inputProps={{ maxLength: 100 }}
          disabled={settings.groups.name.state === 'saving'}
          size="small"
          sx={INPUT_SX}
        />
      </Box>
      <SectionSaveRow
        {...settings.groups.name}
        saveLabel={t('saveName')}
        cancelLabel={t('cancel')}
        savingLabel={t('saving')}
        savedLabel={t('saved')}
        reserveLayout={surface === 'dialog'}
        onSave={saveName}
        onCancel={() => {
          if (surface === 'dialog') focusNameInput();
          settings.cancelName();
        }}
      />
    </SettingsSection>
  );
}

export function DeleteAccountSection({
  onDeleted,
  onBusyChange,
  onRequestDelete,
  accountAlreadyRemoved = false,
  onAccountRemoved,
}: {
  onDeleted?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onRequestDelete?: (action: () => void) => void;
  accountAlreadyRemoved?: boolean;
  onAccountRemoved?: () => void;
} = {}) {
  const { t } = useTranslation('settings');
  const { user, signOut } = useAuth();
  const { showNotification } = useNotification();
  const query = useApi();
  const navigate = useNavigate();
  const surface = useSettingsSurface();
  const controlHeight = useSettingsControlHeight();
  const [showConfirm, setShowConfirm] = useState(accountAlreadyRemoved);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<
    'idle' | 'deleting' | 'deleted' | 'error' | 'signOutError'
  >(accountAlreadyRemoved ? 'signOutError' : 'idle');
  const [accountRemoved, setAccountRemoved] = useState(accountAlreadyRemoved);
  const deletePending = useRef(false);
  const deletionSucceeded = useRef(accountAlreadyRemoved);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const deleteEntryButton = useRef<HTMLButtonElement>(null);
  const restoreDeleteEntryFocus = useRef(false);

  useEffect(() => {
    onBusyChange?.(isDeleting);
    return () => onBusyChange?.(false);
  }, [isDeleting, onBusyChange]);

  useEffect(() => {
    if (showConfirm) {
      cancelButton.current?.focus();
    } else if (restoreDeleteEntryFocus.current) {
      deleteEntryButton.current?.focus({ preventScroll: true });
      restoreDeleteEntryFocus.current = false;
    }
  }, [showConfirm]);

  const deleteAccount = useCallback(async () => {
    if (deletePending.current) return;
    deletePending.current = true;
    setIsDeleting(true);
    try {
      if (!deletionSucceeded.current) {
        setDeleteStatus('deleting');
        if (surface === 'page') showNotification(t('deletingAccount'), 'info');
        await query('/user', { method: 'DELETE', failMessage: t('failedToDelete') });
        // Logout can fail after the account was removed. Retrying that step must
        // never resend DELETE or report that the successful deletion failed.
        deletionSucceeded.current = true;
        setAccountRemoved(true);
        onAccountRemoved?.();
        setDeleteStatus('deleted');
        if (surface === 'page') showNotification(t('accountDeleted'), 'success');
      }
      setDeleteStatus('deleted');
      await signOut(surface === 'dialog' ? { throwOnError: true } : undefined);
      if (onDeleted) onDeleted();
      else navigate('/');
    } catch (error) {
      if (deletionSucceeded.current) {
        console.error('Error signing out after deleting account:', error);
        setDeleteStatus('signOutError');
        if (surface === 'page') showNotification(t('accountDeletedSignOutFailed'), 'error');
      } else {
        console.error('Error deleting account:', error);
        setDeleteStatus('error');
        if (surface === 'page') showNotification(t('failedToDelete'), 'error');
      }
    } finally {
      deletePending.current = false;
      setIsDeleting(false);
    }
  }, [navigate, onDeleted, onAccountRemoved, query, showNotification, signOut, surface, t]);

  if (!user) return null;

  return (
    <SettingsSection sx={{ borderTop: `1px solid ${semanticColors.borderLight}`, pt: 2 }}>
      <SettingsRow
        label={t('deleteAccount')}
        description={t('deleteAccountDesc')}
        headerIcon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
        divider={false}
        control={
          !showConfirm ? (
            <Button
              ref={deleteEntryButton}
              color="error"
              size="small"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => {
                const openConfirmation = () => {
                  setDeleteStatus('idle');
                  setShowConfirm(true);
                };
                if (onRequestDelete) onRequestDelete(openConfirmation);
                else openConfirmation();
              }}
              sx={{ textTransform: 'none', borderRadius: BORDER_RADIUS, height: controlHeight }}
            >
              {t('deleteAccount')}
            </Button>
          ) : null
        }
      />
      <Box sx={{ mt: showConfirm ? 2 : 0 }}>
        {showConfirm && !accountRemoved && (
          <Typography sx={{ fontSize: '13px', lineHeight: 1.6, color: semanticColors.textBody }}>
            {t('deleteAccountConfirm')}
          </Typography>
        )}
        {surface === 'dialog' && (
          <Box role="status" aria-live="polite" aria-atomic="true">
            {deleteStatus !== 'idle' && (
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.5,
                  mt: 1,
                  color:
                    deleteStatus === 'error' || deleteStatus === 'signOutError'
                      ? semanticColors.dangerHover
                      : semanticColors.textBody,
                }}
              >
                {t(
                  deleteStatus === 'deleting'
                    ? 'deletingAccount'
                    : deleteStatus === 'deleted'
                      ? 'accountDeleted'
                      : deleteStatus === 'signOutError'
                        ? 'accountDeletedSignOutFailed'
                        : 'failedToDelete'
                )}
              </Typography>
            )}
          </Box>
        )}
        {showConfirm && (
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mt: 2 }}
          >
            {!accountRemoved && (
              <Button
                ref={cancelButton}
                size="small"
                onClick={() => {
                  restoreDeleteEntryFocus.current = true;
                  setShowConfirm(false);
                  setDeleteStatus('idle');
                }}
                disabled={isDeleting}
                sx={{
                  textTransform: 'none',
                  height: controlHeight,
                  color: semanticColors.textSecondary,
                }}
              >
                {t('cancel')}
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={isDeleting}
              onClick={() => {
                const deleteConfirmedAccount = () => void deleteAccount();
                if (onRequestDelete) onRequestDelete(deleteConfirmedAccount);
                else deleteConfirmedAccount();
              }}
              startIcon={isDeleting ? <CircularProgress size={12} color="inherit" /> : undefined}
              sx={{
                textTransform: 'none',
                borderRadius: BORDER_RADIUS,
                height: controlHeight,
                boxShadow: 'none',
              }}
            >
              {t(accountRemoved ? 'signOut' : 'yesDeleteAccount')}
            </Button>
          </Box>
        )}
      </Box>
    </SettingsSection>
  );
}
